import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  clearPaywallCheckoutPassword,
  clearPaywallPayload,
  getCheckoutStatus,
  loadPaywallCheckoutPassword,
  loadPaywallPayload,
  startPaymobCheckout,
  type PaywallPayload,
} from "../api/billing";
import { LangSwitch } from "../components/layout/LangSwitch";
import { formatMinor } from "../lib/money";
import { ApiClientError } from "../api/types";

/** Poll interval for checkout status after Paymob redirect (ms). */
const POLL_MS = 4000;
/** Stop polling after this many seconds. */
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

export function PaywallPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [payload] = useState<PaywallPayload | null>(() => loadPaywallPayload());
  const [password, setPassword] = useState(() => loadPaywallCheckoutPassword() ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Paymob embedded checkout
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [paid, setPaid] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStart = useRef<number>(0);

  useEffect(() => {
    if (!payload) navigate("/login", { replace: true });
  }, [payload, navigate]);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
    setPolling(false);
  }, []);

  const checkStatus = useCallback(
    async (cid: string) => {
      if (Date.now() - pollStart.current > POLL_TIMEOUT_MS) {
        stopPolling();
        setError(t("billing.poll_timeout"));
        return;
      }
      try {
        if (!payload) return;
        const status = await getCheckoutStatus(cid, {
          orgId: payload.orgId,
          email: payload.email,
        });
        if (status.status === 1) {
          // Paid!
          stopPolling();
          setPaid(true);
          clearPaywallPayload();
          clearPaywallCheckoutPassword();
          return;
        }
        if (status.status === 2) {
          stopPolling();
          setError(t("billing.payment_failed"));
          return;
        }
      } catch {
        // ignore network errors during polling, keep trying
      }
      pollTimer.current = setTimeout(() => void checkStatus(cid), POLL_MS);
    },
    [payload, stopPolling, t],
  );

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  if (!payload) return null;
  const paywall = payload;
  const whatsappUrl = `https://wa.me/${paywall.supportWhatsapp.replace(/\D/g, "")}`;

  async function handlePaymobCheckout() {
    setError(null);
    if (!password.trim()) {
      setError(t("billing.password_required"));
      return;
    }
    setSubmitting(true);
    try {
      const res = await startPaymobCheckout({
        orgId: paywall.orgId,
        email: paywall.email,
        password,
      });
      clearPaywallCheckoutPassword();
      setCheckoutId(res.checkoutId);
      setIframeUrl(res.iframeUrl);

      // Start polling for payment confirmation
      pollStart.current = Date.now();
      setPolling(true);
      pollTimer.current = setTimeout(() => void checkStatus(res.checkoutId), POLL_MS);
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("billing.checkout_failed"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  function handleBackToLogin() {
    stopPolling();
    setIframeUrl(null);
    setCheckoutId(null);
    clearPaywallPayload();
    clearPaywallCheckoutPassword();
    navigate("/login", { replace: true });
  }

  function handleCancelPayment() {
    stopPolling();
    setIframeUrl(null);
    setCheckoutId(null);
    setError(null);
  }

  // ── Paid success screen ──────────────────────────────────────────────────
  if (paid) {
    return (
      <div dir="rtl" className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 to-godo-primary px-4 py-8">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-4xl">
            ✅
          </div>
          <h1 className="text-2xl font-bold text-slate-900">{t("billing.payment_success_title")}</h1>
          <p className="mt-2 text-sm text-slate-600">{t("billing.payment_success_hint")}</p>
          <button
            type="button"
            onClick={() => navigate("/login", { replace: true })}
            className="mt-6 w-full rounded-lg bg-godo-accent py-3 text-sm font-semibold text-white transition hover:bg-emerald-700"
          >
            {t("billing.go_to_login")}
          </button>
        </div>
      </div>
    );
  }

  // ── Embedded Paymob checkout ─────────────────────────────────────────────
  if (iframeUrl && checkoutId) {
    return (
      <div dir="rtl" className="min-h-screen bg-gradient-to-br from-slate-900 to-godo-primary px-4 py-6">
        <div className="mx-auto w-full max-w-3xl">
          <div className="mb-4 flex items-center justify-between rounded-2xl bg-white/95 px-4 py-3 shadow-lg">
            <div>
              <h1 className="text-lg font-bold text-slate-900">{t("billing.paymob_screen_title")}</h1>
              <p className="text-xs text-slate-600">
                {paywall.companyName} · {formatMinor(paywall.quote.totalMinor)} {paywall.quote.currency}
              </p>
            </div>
            <LangSwitch />
          </div>

          <div className="overflow-hidden rounded-2xl bg-white shadow-xl">
            <iframe
              title="Paymob checkout"
              src={iframeUrl}
              className="h-[min(78vh,720px)] w-full border-0"
              allow="payment *"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>

          {polling && (
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              <p className="font-medium">{t("billing.paymob_embedded_hint")}</p>
              <p className="mt-1 text-xs">{t("billing.paymob_poll_hint")}</p>
              <p className="mt-2 text-xs text-blue-500" dir="ltr">Ref: {checkoutId}</p>
            </div>
          )}

          {error && (
            <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleCancelPayment}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {t("billing.back_to_quote")}
            </button>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
            >
              {t("billing.contact_whatsapp")}
            </a>
            <button
              type="button"
              onClick={handleBackToLogin}
              className="text-sm text-slate-300 hover:underline"
            >
              {t("billing.back_login")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main paywall screen ──────────────────────────────────────────────────
  return (
    <div dir="rtl" className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 to-godo-primary px-4 py-8">
      <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl">
        <div className="mb-4 flex justify-end">
          <LangSwitch />
        </div>

        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-slate-900">{t("billing.paywall_title")}</h1>
          <p className="mt-2 text-sm text-slate-600">{t("billing.paywall_subtitle")}</p>
        </div>

        {/* Company info */}
        <div className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm">
          <p>
            <span className="font-medium text-slate-700">{t("billing.company")}: </span>
            {paywall.companyName}
          </p>
          <p className="mt-1" dir="ltr">
            <span className="font-medium text-slate-700">{t("billing.email")}: </span>
            {paywall.email}
          </p>
        </div>

        {/* Quote lines */}
        <div className="mb-6 space-y-2">
          {paywall.quote.lines.map((line) => (
            <div
              key={line.code}
              className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm"
            >
              <span>{t(line.labelKey)}</span>
              <span dir="ltr" className="font-semibold text-godo-navy">
                {formatMinor(line.amountMinor)} {paywall.quote.currency}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-slate-200 pt-3 text-base font-bold">
            <span>{t("billing.total_monthly")}</span>
            <span dir="ltr" className="text-godo-accent">
              {formatMinor(paywall.quote.totalMinor)} {paywall.quote.currency}
            </span>
          </div>
        </div>

        {error && (
          <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-slate-700">
            {t("billing.confirm_password")}
          </label>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-godo-accent focus:ring-2 focus:ring-emerald-200"
          />
          <p className="mt-1 text-xs text-slate-500">{t("billing.confirm_password_hint")}</p>
        </div>

        <button
          type="button"
          disabled={submitting}
          onClick={() => void handlePaymobCheckout()}
          className="mb-3 w-full rounded-lg bg-godo-accent py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
        >
          {submitting ? t("billing.processing") : t("billing.pay_paymob")}
        </button>

        {/* WhatsApp support */}
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mb-4 flex w-full items-center justify-center rounded-lg border border-emerald-300 bg-emerald-50 py-2.5 text-sm font-medium text-emerald-800 transition hover:bg-emerald-100"
        >
          {t("billing.contact_whatsapp")}
        </a>

        <button
          type="button"
          onClick={handleBackToLogin}
          className="w-full text-sm text-slate-600 hover:underline"
        >
          {t("billing.back_login")}
        </button>

        <p className="mt-4 text-center text-xs text-slate-500">{t("billing.paywall_note")}</p>
      </div>
    </div>
  );
}
